import { CombinedState, createSelector } from '@reduxjs/toolkit'
import { useFlag, useLDClient } from '@afterpaytouch/feature'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Cohort } from '@afterpaytouch/portal-api'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { PaymentType } from '@afterpay/types'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import {
  CheckoutAppSelector,
  CheckoutAppState,
  CheckoutAppStore,
  ExperimentsState,
  triggerExperiment,
  useActiveCashAppPay,
  useAppDispatch,
  useAppSelector,
  useCashLoginType,
  useCheckoutToken,
  useCohort,
  useConsumerFeatureFlagReady,
  useConsumerLendingEligible,
  useConsumerSelector,
  useIsConsumerLendingOrder,
  useIsEmptyCheckoutToken,
  useIsRecognizedViaPersistentLogin,
  useIsTrustedCheckout,
  usePreferredCard,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldUseBankAccount,
} from '../'
import { Experiment, Experiments, ExperimentValue, Features, getExperiment, KnownExperiment } from '../../model'
import { useStore } from 'react-redux'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { consumeInteractiveLoginSignal, peekInteractiveLoginSignal } from '../../flow/remember-me/loginSignal'

export const experimentsSelector: CheckoutAppSelector<ExperimentsState> = (state) => state.experiments

export const activeExperiments: CheckoutAppSelector<string> = createSelector(experimentsSelector, ({ activeExperiments }) => {
  return Object.entries(activeExperiments)
    .map(([experiment, value]) => `${experiment}${value}`)
    .join(',')
})

const activeExperimentsSelector = (state: CombinedState<CheckoutAppState>): Partial<Record<KnownExperiment, ExperimentValue>> => {
  return state.experiments.activeExperiments
}

export const useExperimentsState = (): ExperimentsState => useAppSelector(experimentsSelector)

export const useActiveExperiments = (): string => useAppSelector(activeExperiments)

export const useTriggerExperimentEvent = (): ((params: Experiment, customEventProperties?: Record<string, unknown>) => void) => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const isEmptyCheckoutToken: boolean = useIsEmptyCheckoutToken()
  return ({ name, value, control }: Experiment, customEventProperties?: Record<string, unknown>): void => {
    const activeExperiments = activeExperimentsSelector(store.getState())
    const analyticsFired = name in activeExperiments
    if (!analyticsFired && !isEmptyCheckoutToken) {
      dispatch(triggerExperiment([name, value]))
      logEvent('Triggered Experiment', {
        experimentName: Experiments[name],
        experimentValue: value,
        experimentControl: control,
        experimentFlag: `${name}${value}`,
        ...customEventProperties,
      })
    }
  }
}

export const useExperimentTrigger = (experiment: Experiment): ((customEventProperties?: Record<string, unknown>) => void) => {
  const [fire, setFired] = useState(false)
  const [customEventProperties, setCustomEventProperties] = useState({})
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (experiment.active && !experiment.loading && fire) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }, [experiment.name, experiment.active, experiment.value, fire, triggerExperimentEvent, experiment, customEventProperties])

  return (customEventProperties) => {
    setFired(true)
    if (customEventProperties !== undefined) {
      setCustomEventProperties(customEventProperties)
    }
  }
}

// delays experiment trigger until experiment is passed in
export const useDelayedExperimentTrigger = (): ((updatedExperiment: Experiment) => void) => {
  const [{ isFired, givenExperiment }, setFiredExperiment] = useState<{ isFired: boolean; givenExperiment: Experiment | undefined }>({
    isFired: false,
    givenExperiment: undefined,
  })
  const triggerExperimentEvent = useTriggerExperimentEvent()

  useEffect(() => {
    if (givenExperiment === undefined) {
      return
    }

    if (givenExperiment.active && !givenExperiment.loading && isFired) {
      triggerExperimentEvent(givenExperiment)
    }
  }, [givenExperiment, givenExperiment?.name, givenExperiment?.active, givenExperiment?.value, givenExperiment?.control, isFired, triggerExperimentEvent])

  return (experiment) =>
    setFiredExperiment({
      isFired: true,
      givenExperiment: experiment,
    })
}

export function useExperiment(name: KnownExperiment, fireEvent: boolean = true, customisedExperiment: Experiment | null = null): Experiment {
  const client = useLDClient()
  const consumerFeatureFlagReady = useConsumerFeatureFlagReady()
  const { activeExperiments } = useExperimentsState()
  let experiment = customisedExperiment
  if (experiment === null) {
    const ldValue = client?.variationDetail(Experiments[name], false)
    experiment = getExperiment(name, ldValue, activeExperiments[name])
  }

  const experimentTrigger = useExperimentTrigger(experiment)

  useEffect(() => {
    if (fireEvent && consumerFeatureFlagReady) {
      experimentTrigger()
    }
  }, [consumerFeatureFlagReady, experimentTrigger, fireEvent])

  return experiment
}

// Fires the `Triggered Experiment` exposure for the AP-cohort remember-me experiment (CASHDAP-3556)
// exactly once per fresh interactive login (password / OTP / biometric / 2FA). Mounted on the
// LoggedIn page, where every fresh login converges — deliberately NOT inside the remember-me mint
// hook (`useMintPersistentSessionOnLogin`), whose gating (including the flag check itself) excludes
// the control arm and would bias the enrolled populations; both arms pass through LoggedIn. The
// one-shot login signal (see `loginSignal.ts`) excludes silent persistent-login resume and mid-flow
// re-logins, so the exposure is anchored to the login that just happened. `useExperiment(..., false)`
// resolves the variant without auto-firing, so this hook is the sole trigger.
//
// The flag is targeted per-consumer uuid, and `useExperiment(..., false)` bypasses the readiness
// gate the auto-fire path enforces: LD bootstraps from localStorage, so before the post-login
// identify completes `variation()` can return a stale (anonymous or previous user's) value. Mirror
// the mint's "wait until the client context matches" idiom — hold the pending exposure until the LD
// client's context key is this consumer's uuid AND flags report ready — so the logged variant is
// the resolved current-user assignment.
//
// Dedup is per LOGIN GENERATION, not per experiment-session: `useTriggerExperimentEvent`'s dedup
// keys on the `experiments.activeExperiments` slice, which is never cleared on logout, so routing
// through it would suppress the exposure for a second interactive login in the same tab
// (logout → re-login). Instead we log directly and PEEK the signal while gates resolve, only
// CONSUMING it at the moment the event fires — the signal lives in module state, so a pre-fire
// unmount/detour (e.g. ADD_MOBILE) preserves the pending exposure for the next LoggedIn mount,
// while consume-at-fire still bounds it to exactly once per login. We still
// `dispatch(triggerExperiment(...))` so the `activeExperiments` slice (and the derived
// `activeExperiment` enduring property) reflects the assignment.
export const useTriggerRememberMeExperimentExposure = (): void => {
  const experiment = useExperiment('AutoRememberMe', false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const isEmptyCheckoutToken = useIsEmptyCheckoutToken()
  const { uuid, hasFeatureFlagsReady } = useConsumerSelector()
  const isRecognizedViaPersistentLogin = useIsRecognizedViaPersistentLogin()
  const isTrustedCheckout = useIsTrustedCheckout()
  const ldClient = useLDClient()

  useEffect(() => {
    if (!peekInteractiveLoginSignal('remember-me-exposure')) {
      return
    }
    // A remembered session's step-up resume also marks the login signal (checkCode runs after the
    // 207 verify), but its enrollment already fired at the original fresh login — and only the
    // treatment arm can ever resume, so counting resumes would asymmetrically inflate that arm's
    // exposures. Consume the signal without firing. Gate on recognition provenance, NOT
    // status.persistent: this page's own mint flips persistent true mid-mount on a fresh login,
    // which would wrongly suppress genuine fresh-login exposures once LD readiness resolves.
    if (isRecognizedViaPersistentLogin || isTrustedCheckout) {
      consumeInteractiveLoginSignal('remember-me-exposure')
      return
    }
    if (isUndefinedOrEmpty(uuid) || ldClient === undefined) {
      return
    }
    // Until the client's context key matches this consumer AND LD reports ready, the variant could
    // be a stale (localStorage-bootstrapped or pre-login) value. Wait without consuming the signal;
    // the hasFeatureFlagsReady flip re-runs this effect once the app-level identify completes.
    const context = ldClient.getContext?.() as { key?: string } | undefined
    if (context?.key !== uuid || hasFeatureFlagsReady !== true) {
      return
    }
    // Do not fire while the variant is unresolved, when the experiment is inactive (LD targeting
    // OFF / errored / not found — the dark state produces zero events), or without a checkout
    // token, as the standard experiment-trigger path requires. The signal stays unconsumed.
    if (experiment.loading || !experiment.active || isEmptyCheckoutToken) {
      return
    }
    consumeInteractiveLoginSignal('remember-me-exposure')
    const { name, value, control } = experiment
    dispatch(triggerExperiment([name, value]))
    logEvent('Triggered Experiment', {
      experimentName: Experiments[name],
      experimentValue: value,
      experimentControl: control,
      experimentFlag: `${name}${value}`,
      exposureTrigger: 'login',
    })
  }, [uuid, hasFeatureFlagsReady, ldClient, isEmptyCheckoutToken, isRecognizedViaPersistentLogin, isTrustedCheckout, experiment, dispatch, logEvent])
}

// sessionStorage key recording the checkout token the summary-redesign exposure last fired for, so
// the exposure is deduped per checkout (survives refresh, re-fires for a new token). See the hook.
const SUMMARY_EXPOSURE_TOKEN_KEY = 'pmSummaryRedesignExposureToken'

// Fires the `Triggered Experiment` exposure for the Pay Monthly summary redesign when the consumer
// reaches the Pay Monthly SUMMARY page (CASHDAP-3379). The experiment metric is summary-page
// drop-off, and there are drop-off points between login and the summary, so the exposure is anchored
// to the summary VIEW rather than to login. Mounted once on the summary route (`Summary.tsx`).
//
// Gated on `useIsConsumerLendingOrder` — i.e. the consumer has SELECTED Pay Monthly (PCL) and is
// therefore being routed to the PM summary — so only the population that actually sees the
// experiment surface is enrolled. (At login we used PCL-offer PRESENCE because the payment type
// wasn't chosen yet; at the summary the selection is settled, and the routing that picks the
// redesigned vs legacy summary keys off this same signal.) The exposure fires for BOTH arms —
// treatment (variant A, redesigned summary) and control (variant C, legacy summary) — because
// `experiment.active` is true for either enrolled value (see `getExperiment`); non-enrolled users
// (OFF / ERROR) stay inactive and never log.
//
// The variant it reports is the PINNED routing decision from `Summary.tsx` — the page the consumer
// actually saw — not a fresh LD read: the two can diverge for a moment around the post-start
// re-identify, and a mid-page flag change must not relabel a consumer who already saw the other
// page. (The previous live-read + "wait until the LD context matches `started.hasOrderHistory`"
// gate solved the same mislabel risk by delaying the log, but an identify that never completed
// blocked the exposure for the whole session while the consumer's events flowed unmarked.)
// `active`/`loading` still come LIVE from LD via `useExperiment(..., false)` (no auto-fire), so
// OFF / ERROR / missing-flag sessions never log — turning the flag off remains the kill switch.
//
// `pinnedByTimeout` marks a pin that came from the loader TIMEOUT rather than an LD resolution:
// the consumer was routed to legacy because LD was slow/unavailable, not because they're in arm C.
// Those sessions are never logged (see the gate below).
export const useTriggerSummaryExperimentExposure = (pinnedRedesignEnabled: boolean | null, pinnedByTimeout: boolean = false): void => {
  const liveExperiment = useExperiment('PayMonthlySummaryRedesign', false)
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const token = useCheckoutToken()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const store: CheckoutAppStore = useStore()
  const hasFiredRef = useRef(false)

  const experiment: Experiment = useMemo(
    () => (pinnedRedesignEnabled === null ? liveExperiment : { ...liveExperiment, value: pinnedRedesignEnabled ? 'A' : 'C', control: !pinnedRedesignEnabled }),
    [liveExperiment, pinnedRedesignEnabled]
  )

  useEffect(() => {
    // Fire at most once per mount (ref) — primarily a render-loop guard: dispatching the assignment
    // below re-renders this hook (it reads the activeExperiments slice), and the guard stops the
    // effect re-running into a max-update-depth loop (also covers the case where sessionStorage throws).
    if (hasFiredRef.current) {
      return
    }
    // Only the PM summary surfaces the experiment. Apple Retail PCL orders are consumer-lending but
    // render the Apple Retail summary: Summary.tsx pins that route and passes a NULL pinned variant
    // for it, so the null gate below IS the Apple Retail exclusion. Deliberately no live
    // isAppleRetailOrder read here — that flag-gated value can flap with LD churn and would suppress
    // valid exposures for pageviews that pinned (and kept showing) a PM page. Trust the pin.
    //
    // A timeout pin is excluded outright: the legacy page it routed to reflects LD being unresolved,
    // not the consumer's assignment, and if LD readies later in the same pageview a variant-A
    // consumer would get logged as 'C' — a one-directional contamination of the control arm with
    // slow-network sessions. Skipping mirrors how an LD-that-never-readies session behaves, and
    // since the sessionStorage token below is never written, a refresh with LD healthy re-pins from
    // the real flag and fires a correctly-labelled exposure.
    if (
      pinnedByTimeout ||
      !isConsumerLendingOrder ||
      !hasFeatureFlagsReady ||
      pinnedRedesignEnabled === null ||
      experiment.loading ||
      !experiment.active ||
      token.length === 0
    ) {
      return
    }
    hasFiredRef.current = true
    // Dedup per CHECKOUT TOKEN, not session-wide. This exposure represents a PM summary-page VIEW, so
    // a second checkout (a new token) in the same tab must re-record it. The fired token is kept in
    // sessionStorage: a manual refresh of the SAME checkout does NOT re-fire, a NEW token DOES, and a
    // new browser session starts clean. We therefore log directly rather than through the
    // `useTriggerExperimentEvent` slice dedup, which keys on `experiments.activeExperiments` — that
    // slice is session-wide and never cleared per checkout, so it would drop the second exposure.
    try {
      if (window.sessionStorage.getItem(SUMMARY_EXPOSURE_TOKEN_KEY) === token) {
        return
      }
      window.sessionStorage.setItem(SUMMARY_EXPOSURE_TOKEN_KEY, token)
    } catch {
      // sessionStorage unavailable (e.g. private mode) — fall through and log once for this mount.
    }
    const { name, value, control } = experiment
    dispatch(triggerExperiment([name, value]))
    logEvent('Triggered Experiment', {
      experimentName: Experiments[name],
      experimentValue: value,
      experimentControl: control,
      experimentFlag: `${name}${value}`,
      exposureTrigger: 'summary',
      // Call-level prop overriding the enduring `activeExperiment`, which was render-captured
      // BEFORE the dispatch above — without this the exposure is the one event of the checkout
      // that misses its own experiment marker (read post-dispatch, so it includes it).
      activeExperiment: activeExperiments(store.getState()),
    })
  }, [pinnedByTimeout, isConsumerLendingOrder, hasFeatureFlagsReady, pinnedRedesignEnabled, token, experiment, dispatch, logEvent, store])
}

// Fires the `Triggered Experiment` exposure for the inline Cash login experiment (CASHDAP-3181).
// Mounted on BOTH destination surfaces of the routing decision in `useCashAuthRoute` — the grant/QR
// page (`CashAuth`, control arm C) and the inline flow (`CashInlineAuth`, treatment arm A) — so the
// exposure is anchored to actually reaching a cash-auth surface, and both arms enroll.
//
// Gated on `useCashLoginType() === 'inline'`: only inline-capable consumers are bifurcated by the
// flag. `loginType === 'grant-flow'`/undefined users land on /cash-auth regardless of variant, so
// enrolling them would dilute the analysis population with users who cannot experience treatment.
//
// While the LD flag's targeting is OFF the evaluation reason is OFF → `experiment.active` is false
// (see `getExperiment`) and nothing fires — today's dark state produces zero events. Dispatches via
// `useTriggerExperimentEvent`, whose `activeExperiments` slice dedup fires once per session
// (whichever surface mounts first) and requires a non-empty checkout token. The ref guard bounds
// this mount to a single dispatch — `useTriggerExperimentEvent` returns a fresh closure every
// render, so without it the custom-properties object would re-run the effect indefinitely.
export const useTriggerCashInlineLoginExperimentExposure = (): void => {
  const experiment = useExperiment('CashInlineLogin', false)
  const loginType = useCashLoginType()
  const hasFeatureFlagsReady = useConsumerFeatureFlagReady()
  const triggerExperimentEvent = useTriggerExperimentEvent()
  const hasFiredRef = useRef(false)

  useEffect(() => {
    if (hasFiredRef.current) {
      return
    }
    if (loginType !== 'inline' || !hasFeatureFlagsReady || experiment.loading || !experiment.active) {
      return
    }
    hasFiredRef.current = true
    triggerExperimentEvent(experiment, { exposureTrigger: 'cash-auth' })
  }, [loginType, hasFeatureFlagsReady, experiment, triggerExperimentEvent])
}

export const useIsEligibleForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  // Mirrors the routing in LoggedIn.tsx — only users who would actually be sent down the
  // experimental no-payment-method branch should be enrolled, otherwise variant A and control
  // behave identically and dilute the analysis population. `selectPreferredCard` collapses the
  // full saved-card / fallback state into preferredCard.card: it's null only when the user has
  // no preferred and no usable fallback, and non-null in every other case (including the
  // errored-preferred-with-no-fallback edge), so this check matches LoggedIn's
  // `preferredCard?.card !== null` routing guard exactly.
  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    addPaymentMethodFlowExperimentValue === 'A',
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    !isPaymentMethodEligible,
    hasNoUsablePaymentMethod,
  ]
  return eligibilityConditions.every((condition) => condition)
}

// Symmetric to useIsEligibleForAddPaymentMethodFlowExperiment but for the control arm. Returns
// true iff the user is a variant-C participant who would be routed to the standard
// add-payment-method page in LoggedIn.tsx. Same cohort/design/lending/no-payment-method gates
// as Test — the only difference is the variant check. Also requires `experiment.active` and
// !experiment.loading because `getExperiment` defaults `value` to 'C' while LD is loading
// (`apps/checkout/src/model/experiments.tsx`'s `loading` constant), and we must not enroll
// users whose variant assignment hasn't actually resolved yet.
//
// Background: Amplitude variant-C `Triggered Experiment` events dropped from ~30-60/day to 0
// on 2026-05-06 (the day PR #11859 deployed). The original eligibility hook only ever fires
// the trigger for variant=A users, so it can't have been the source of the pre-2026-05-06
// variant-C events — that means some other implicit mechanism was firing them and stopped.
// Restoring symmetric explicit firing here closes the gap without depending on whatever
// implicit mechanism existed before; the dispatcher's analyticsFired dedup in
// useTriggerExperimentEvent bounds any future double-firing to one event per session.
export const useIsControlForAddPaymentMethodFlowExperiment = (): boolean => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value, active, loading } = experiment
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isPaymentMethodEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  return active && !loading && value === 'C' && cohort !== Cohort.CashOnly && !isBronteDesignLanguage && !isPaymentMethodEligible && hasNoUsablePaymentMethod
}

const usePi4SwitcherNoPaymentMethodExperimentEligibility = (
  selectedPaymentType?: PaymentType
): { eligible: boolean; experimentValue: ExperimentValue; experimentActive: boolean; experimentLoading: boolean } => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const { value: addPaymentMethodFlowExperimentValue, active, loading } = experiment
  const isPi4SwitcherSummaryFirstEnabled = useFlag(Features.CHECKOUT_ENABLE_ADD_PAYMENT_METHOD_PM_PI4_SWITCHER_SUMMARY_FIRST, false)
  const cohort = useCohort()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const preferredCard = usePreferredCard()
  const cashAppPay = useActiveCashAppPay()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const shouldUseBankAccount = useShouldUseBankAccount()

  const hasNoUsablePaymentMethod = preferredCard?.card === null && cashAppPay.length === 0 && !shouldUseApplePay && !shouldUseBankAccount

  const eligibilityConditions = [
    isPi4SwitcherSummaryFirstEnabled,
    selectedPaymentType === PaymentType.PBI,
    cohort !== Cohort.CashOnly,
    !isBronteDesignLanguage,
    isConsumerLendingEligible,
    hasNoUsablePaymentMethod,
  ]

  return {
    eligible: eligibilityConditions.every((condition) => condition),
    experimentValue: addPaymentMethodFlowExperimentValue,
    experimentActive: active,
    experimentLoading: loading,
  }
}

export const useShouldTriggerPi4SwitcherWithNoPaymentMethodExperiment = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && (experimentValue === 'A' || experimentValue === 'C')
}

export const useShouldRoutePi4SwitcherWithNoPaymentMethodToSummary = (selectedPaymentType?: PaymentType): boolean => {
  const { eligible, experimentValue, experimentActive, experimentLoading } = usePi4SwitcherNoPaymentMethodExperimentEligibility(selectedPaymentType)

  return eligible && experimentActive && !experimentLoading && experimentValue === 'A'
}

// Returns a callback that fires the Triggered Experiment event for the current add-payment-method
// experiment assignment. Caller is responsible for invoking this only when the user is actually
// routed through a no-payment-method branch (currently from LoggedIn.tsx, gated on requestCompleted +
// hasFeatureFlagsReady). The eligibility hooks above intentionally do NOT auto-fire because
// `preferredCard?.card === null` can be transiently true (e.g. after `resetPreferredPaymentMethodErrors`
// mutates the card field, or before `setCardsForExpiryLimit` runs with a populated `started` payload),
// which previously enrolled users with saved cards and diluted the variant-A pool.
//
// Dispatches synchronously rather than using the `useExperimentTrigger` setState+useEffect
// indirection: callers invoke this immediately before `router.replace`, so the LoggedIn page
// can unmount in the next render and skip a deferred effect. Calling `useTriggerExperimentEvent`
// directly guarantees the Redux dispatch + analytics logEvent happen in the same execution as
// the trigger() invocation.
export const useAddPaymentMethodFlowExperimentTrigger = (): ((customEventProperties?: Record<string, unknown>) => void) => {
  const experiment = useExperiment('CheckoutAddPaymentMethodFlowExperiment', false)
  const triggerExperimentEvent = useTriggerExperimentEvent()
  return (customEventProperties) => {
    if (experiment.active && !experiment.loading) {
      triggerExperimentEvent(experiment, customEventProperties)
    }
  }
}
